import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { DisplayElementModel } from '../../../Common/PageBuilder/models/display-element.model';
import { BriefingBlockTypeEnum, briefingBlockTypeEnumToApi } from '../enums/briefing-block-type.enum';
import { MonitoringTextStyleModel } from '../../Shared/models/monitoring-text-style.model';
import { CuratedMentionListDisplayElementModel } from './curated-mention-list-display-element.model';
import { TableOfContentBlockDataModel } from './table-of-content-block-data.model';
import { TableOfContentSettingsModel } from './table-of-content-settings.model';
import { TableOfContentMentionModel } from './table-of-content-mention.model';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';
import { BriefingHeadingLevelEnum } from '../enums/briefing-heading-level.enum';
import { CuratorBlock } from '../../../Common/PageBuilder/models/curator-block.model';

export class TableOfContentDisplayElementModel extends DisplayElementModel {
    declare styles: MonitoringTextStyleModel;
    declare settings: TableOfContentSettingsModel;
    declare isBoundToGlobalStyles: boolean;
    briefingBlockSummaryData: TableOfContentBlockDataModel[];

    constructor(
        styles?: any,
        settings?: any
    ) {
        super();

        this.styles = styles && styles.clone ? styles.clone() : new MonitoringTextStyleModel(styles);
        this.settings = settings && settings.clone ? settings.clone() : new TableOfContentSettingsModel(settings);
        this.isBoundToGlobalStyles = true;
    }

    canRender(): boolean {
        if (!this.briefingBlockSummaryData || !this.settings) {
            return false;
        }

        return this.briefingBlockSummaryDataHasIncludedContent();
    }

    clone(): TableOfContentDisplayElementModel {
        const clone = new TableOfContentDisplayElementModel(this.styles, this.settings);
        clone.displayType = this.displayType;

        return clone;
    }

    onUpdate(): boolean {
        return true;
    }

    isEqual(other: TableOfContentDisplayElementModel): boolean {
        if (!other) {
            return false;
        }

        if (!this.styles.equals(other.styles, true)) {
            return false;
        }

        if (!this.settings.equals(other.settings)) {
            return false;
        }

        return true;
    }

    /**
     * Sets data for briefing blocks to be displayed in table of content
     */
    setBrifeingBlockSummaryData(blocks: BlockModel[]): void {
        this.briefingBlockSummaryData = [];
        let index = 0;
        blocks.forEach((block) => {
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach((column, j) => {
                    column.forEach(x => {
                        index++;
                        this.briefingBlockSummaryData.push(this.setTOCBlockDataModel(x, index));
                    });
                });
            } else if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (block as CuratorBlock).blocks.forEach((x) => {
                    index++;
                    this.briefingBlockSummaryData.push(this.setTOCBlockDataModel(x, index));
                });
            } else {
                this.briefingBlockSummaryData.push(this.setTOCBlockDataModel(block, index));
            }
            index++;
        });
    }

    setTOCBlockDataModel(block: BlockModel, index: number): TableOfContentBlockDataModel {
        const type = <BriefingBlockTypeEnum>block.type.key;
        let title = (<any>block.displayElement).title;
        let mentions;
        let level;

        if (type === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
            mentions = (<CuratedMentionListDisplayElementModel>block.displayElement).mentions.map(mention => {
                return new TableOfContentMentionModel(mention.title, mention.mediaType);
            });
        }

        if (type === BriefingBlockTypeEnum.TITLE) {
            title = (<any>block.displayElement).text;

            const briefingTitleLevel = (<any>block.displayElement).headingLevel;
            if (briefingTitleLevel === BriefingHeadingLevelEnum.HEADING_1) {
                level = 1;
            } else if(briefingTitleLevel === BriefingHeadingLevelEnum.HEADING_2) {
                level = 2;
            } else {
                level = 3;
            }
        }

        if (type === BriefingBlockTypeEnum.CHART) {
            title = (<any>block.displayElement)?.chartBlock?.displayElement?.title;
        }

        return new TableOfContentBlockDataModel(
            briefingBlockTypeEnumToApi.get(type),
            title,
            mentions,
            index,
            level
        );
    }

    /**
     * Returns true if table of contents has any content to display
     */
    briefingBlockSummaryDataHasIncludedContent(): boolean {
        return !!this.briefingBlockSummaryData.find(blockData => this.settings.isBlockTypeIncludedBySettings(blockData.type));
    }
}
